import { GetServerSideProps } from "next";

const apiBaseUrl = process.env.NEXT_PUBLIC_REACT_APP_API_BASE_URL;
const sitemapBaseUrl = process.env.NEXT_PUBLIC_SITEMAP_BASE_URL;

// List of paths to exclude from the sitemap
const NOT_ALLOW: string[] = [
  "technologies/hire-the-best-aws-solution-architect",
  "blog/newsroom",
];

// These content types have their own dedicated category sitemaps, or should not be indexed at all
const EXCLUDED_KEYS: string[] = [
  "blog",
  "case-studies",
  "careers",
  "whitepaper",
  "newsroom",
  "use-cases",
  "services",
  "services-2025",
  "industries",
  "solutions",
];

// Keys whose items render at the site root (e.g. /about-us), not under a "<key>/" prefix
const ROOT_LEVEL_KEYS: string[] = ["pages", "ghost"];

// Some collections include a self-referencing wrapper entry (e.g. "our-leadership" inside the
// our-leadership list itself) that duplicates the category's own landing page — skip those.
const SELF_SLUG_KEYS: string[] = ["our-leadership"];

// Root-level slugs that already have their own dedicated category sitemap, or shouldn't be indexed
const ROOT_LEVEL_EXCLUDED_SLUGS: string[] = ["careers", "home"];

// Function to generate the sitemap XML
const generatePagesSiteMap = (sitemapUrls: string[]) => {
  return `<?xml version="1.0" encoding="UTF-8"?>
        <?xml-stylesheet type="text/xsl" href="${sitemapBaseUrl}main-sitemap.xsl"?>
        <urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
            ${sitemapUrls
              .map(
                (url: string) => `
                    <url>
                        <loc>${url}</loc>
                        <changefreq>weekly</changefreq>
                        <priority>0.8</priority>
                    </url>
                `
              )
              .join("")}
        </urlset>
    `;
};

// Server-side function to generate the sitemap
export const getServerSideProps: GetServerSideProps<
  Record<string, unknown>
> = async ({ res }) => {
  try {
    // Fetch all pages from the API
    const request = await fetch(`${apiBaseUrl}all-pages`);
    const slugs = await request.json();

    // Initialize sitemap URLs with static pages
    let sitemapUrls = [
      `${sitemapBaseUrl}`,
      `${sitemapBaseUrl}request-proposal`,
      `${sitemapBaseUrl}terms-of-service`,
      `${sitemapBaseUrl}privacy-policy`,
      `${sitemapBaseUrl}thank-you`,
      `${sitemapBaseUrl}vendor-registration`,
    ];

    // Add dynamic URLs from the API response
    Object.keys(slugs).forEach((key: string) => {
      if (!slugs[key].length || EXCLUDED_KEYS.includes(key)) return;

      const normalizedKey = key.replace("-2025", "");

      slugs[key].forEach((item: any) => {
        if (SELF_SLUG_KEYS.includes(normalizedKey) && item === normalizedKey) return;

        if (ROOT_LEVEL_KEYS.includes(normalizedKey)) {
          if (ROOT_LEVEL_EXCLUDED_SLUGS.includes(item)) return;
          sitemapUrls.push(`${sitemapBaseUrl}${item}`);
        } else if (item && typeof item === "object" && item.slug) {
          sitemapUrls.push(`${sitemapBaseUrl}${normalizedKey}/${item.slug}`);
        } else {
          sitemapUrls.push(`${sitemapBaseUrl}${normalizedKey}/${item}`);
        }
      });
    });

    // ✅ Filter URLs to exclude those in the NOT_ALLOW list
    sitemapUrls = sitemapUrls.filter(
      (url) => !NOT_ALLOW.some((path) => url.includes(path))
    );

    // Remove duplicates (e.g. "request-proposal" is both a static entry and an item in the "pages" key)
    sitemapUrls = Array.from(new Set(sitemapUrls));

    // Generate the sitemap XML
    const sitemap = generatePagesSiteMap(sitemapUrls);

    // Set headers and send the response
    res.setHeader("Content-Type", "text/xml");
    res.write(sitemap);
    res.end();
  } catch (error) {
    console.error("Error generating sitemap:", error);
    res.statusCode = 500;
    res.end();
  }

  return {
    props: {},
  };
};

// Default export (empty component)
export default function Sitemap() {
  return null;
}
